/* eslint-disable complexity */
/* eslint-disable max-statements */
import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';

import { usePdpContext } from '../../pdp-provider';

import SelectWrapper from './select-wrapper';
import messages from './messages';

const ModifierSelect = ({
  categoryKeyPath,
  productOptionForm,
  productOptionNumber,
  topLevelCategoryName,
  topLevelCategoryNumber,
  useInactiveStyle,
}) => {
  const { formatMessage } = useIntl();
  const {
    isDemystifyMenuPdpEnabled,
    productOptionDefaults,
    selectedOptions,
    pricedSelectedOptions,
    updateProductOption,
  } = usePdpContext();
  const currentProductOptionDefaultObject =
    productOptionDefaults[productOptionNumber];

  const preselectedOption = selectedOptions?.find(
    (option) => option.optionProductNumber === productOptionNumber
  );

  const modifiedFromDefault = preselectedOption
    ? !useInactiveStyle &&
      preselectedOption?.size?.sizeCode !==
        currentProductOptionDefaultObject?.defaultSizeCode
    : false;

  const onSelectionHandler = (selection) => {
    const selectionSize = (productOptionForm.sizes || []).find(
      (size) => size.name === selection
    );

    if (selection) {
      updateProductOption({
        categoryKeypath: categoryKeyPath,
        formCode: productOptionForm.formCode,
        optionProductNumber: productOptionNumber,
        size: selectionSize,
        topLevelCategoryNumber,
      });
    }
  };

  const selectedSizeCode =
    preselectedOption?.size?.sizeCode ||
    (currentProductOptionDefaultObject?.inRecipe &&
      currentProductOptionDefaultObject?.defaultSizeCode);
  const selectedSize = (productOptionForm?.sizes || []).find(
    (size) => size.sizeCode === selectedSizeCode
  );
  const productOptionPrice = isDemystifyMenuPdpEnabled
    ? pricedSelectedOptions?.get(preselectedOption?.size?.sku)
    : null;

  const selectedSizeName = selectedSize?.name;
  const getLabel = () => {
    // ModifierSelect is inactive if user sets it back to 'no',
    // (and it's not part of the default recipe),
    // OR if it hasn't been selected yet on the Customizations page.
    // We use a label referencing the form in this case,
    // like "Add whipped cream". Once the user selects one of the choices,
    // (ie extra/light/no/regular), the label moves into the
    // active position, and we want to use the topLevelCategoryName
    // (ie. "Toppings").
    if (useInactiveStyle) {
      return formatMessage(messages.addProductOption, {
        productOptionName: productOptionForm?.name,
      });
    }
    return topLevelCategoryName;
  };

  const optionsList = new Map();
  const productOptionFormSizes = productOptionForm?.sizes || [];

  if (productOptionFormSizes.length) {
    for (const { sku, name, sizeCode } of productOptionFormSizes) {
      optionsList.set(sku, { name, sizeCode });
    }
  }

  return (
    <SelectWrapper
      data-e2e="modifier-select"
      // a unique id can be generated from the product option metadata,
      // since each option of formCode 'modifier' will be shown in a
      // separate Select.
      id={`${productOptionNumber}-modifier-select`}
      isDemystifyMenuPdpEnabled={isDemystifyMenuPdpEnabled}
      modified={modifiedFromDefault}
      onSelection={onSelectionHandler}
      options={optionsList}
      productOptionNumber={productOptionNumber}
      productOptionPrice={productOptionPrice}
      useInactiveStyle={useInactiveStyle}
      // if the option is inactive, a 'no' option like "No whipped cream" does not
      // make the product active, so we ignore it.
      value={
        useInactiveStyle && selectedSizeCode === 'no' ? null : selectedSizeName
      }
    >
      {getLabel()}
    </SelectWrapper>
  );
};

ModifierSelect.propTypes = {
  categoryKeyPath: PropTypes.array,
  topLevelCategoryName: PropTypes.string,
  productOptionForm: PropTypes.object,
  productOptionNumber: PropTypes.number,
};

export default ModifierSelect;
